import { Link } from '@tanstack/react-router';
import { BuildingOfficeIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { DocsLayout } from '../../components/docs/DocsLayout.tsx';
import { DocIcon } from '../../components/docs/DocIcon.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';

export function DocsWorkspace() {
  const t = useT();
  useDocumentTitle(t('docs.workspace.title'));

  return (
    <DocsLayout
      path="/docs/workspace"
      title={t('docs.workspace.label')}
      lede="A workspace is your account and everything under it: the documents you run, the people you run them with, and how you’re billed. This page is the map, and each area links to its full guide."
    >
      <h2 id="intro">Your workspace</h2>
      <p>
        When you sign up, Goalpost creates a <strong>workspace</strong> for you.
        It’s the top-level container for your work: it owns your documents, holds
        your teams, and carries your plan and billing. Think of it as the boundary
        around one organization or one household, where everything inside it
        belongs together.
      </p>
      <p>
        You can belong to more than one workspace. A freelancer might have their
        own workspace plus a seat in a client’s; an employee might work across a
        company workspace and a personal one. Switch between them using the
        workspace switcher at the bottom of the left sidebar. Each workspace is
        completely separate, so documents, teams, and billing never cross between
        them.
      </p>
      <p>
        A workspace keeps administration and access separate. Who can reach what
        comes down to two roles, <strong>administrator</strong> and{' '}
        <strong>member</strong>, described next.
      </p>

      <h2 id="access">Administrators and members</h2>
      <p>
        Everyone in a workspace is either an administrator or a member, and there
        are only two ways to be added to one.
      </p>
      <ul>
        <li>
          <strong>Administrators</strong> run the workspace. They have full access
          to manage its teams, documents, billing, and approval rules. You
          become an administrator by creating a workspace, or by being invited to
          the workspace directly (see Invitations below).
        </li>
        <li>
          <strong>Members</strong> are added by being invited to a team. A member’s
          access flows through their teams: they can see and propose changes to a
          document only when one of their teams has been added to it. Put someone on
          more teams to widen their access, or remove them from a team to narrow it.
        </li>
      </ul>

      <h2 id="teams">Teams</h2>
      <p>
        <strong>Teams</strong> are how you grant document access and how approvals
        are organized: you add a team to a document to let its members in, and you
        can require a team’s sign-off to gate changes.
      </p>
      <p>
        A team can have just one person, and the same team can be added to many
        documents and approval rules without being recreated. Learn more in our{' '}
        <Link to="/docs/teams">Teams guide</Link>.
      </p>

      <h2 id="documents">Documents</h2>
      <p>
        A <strong>document</strong> is a structured list of lists that
        holds your notes, context, reference materials, requirements, and
        decisions, along with links and images, organized with sections and
        labels. Each document is versioned over time. Workspaces can
        hold as many documents as your plan allows, and each document has its own
        contents, team assignments, and approval configuration.
      </p>
      <p>
        Learn more in our <Link to="/docs/documents">Documents guide</Link>.
      </p>

      <h2 id="approval-rules">Approval rules</h2>
      <p>
        Approvals in Goalpost come together from a few layers, and it helps to
        know which is which:
      </p>
      <ul>
        <li>
          <strong>Document rules</strong> (the document’s{' '}
          <em>approval rule</em>) decide which teams have to approve a change
          to that document, and how their approvals combine.
        </li>
        <li>
          <strong>Detail rules</strong> let a specific set of details carry its
          own approval rule, so you can make sure the relevant people are
          involved in the governance of the parts of your document they are
          responsible for.
        </li>
        <li>
          <strong>Team rules</strong> configure how many members of a team need
          to sign off on a team’s approval for it to count: any single member,
          all members, or a set number of them.
        </li>
      </ul>
      <p>
        Together these let a change need, say, one designer and both clients, with
        a tighter rule on the budget section. Learn more in our{' '}
        <Link to="/docs/approval-rules">Approval Rules guide</Link>.
      </p>

      <h2 id="account-types">Account types</h2>
      <p>
        Every workspace is on a plan. Plans set how much you can do and which
        features are available, and you can upgrade or downgrade at any time from
        your workspace settings.
      </p>
      <ul>
        <li>
          <strong>Free:</strong> for individuals and getting started. Run
          documents, build them out, and track their history. Ideal for
          solo planning and trying Goalpost on real work.
        </li>
        <li>
          <strong>Pro:</strong> for teams and clients working together. Adds
          higher document limits and collaboration across multiple teams.
          Both tiers can connect an AI assistant through{' '}
          <Link to="/docs/ai-integration">AI Integrations</Link>.
        </li>
      </ul>
      <p>
        The exact limits and current pricing are on the{' '}
        <Link to="/pricing">Pricing</Link> page.
      </p>

      <h2 id="invitations">Invitations</h2>
      <p>
        You add an <strong>administrator</strong> by sending them a workspace
        invitation link.
      </p>
      <ol>
        <li>
          Go to <strong>Workspace</strong>{' '}
          <DocIcon>
            <BuildingOfficeIcon aria-hidden="true" />
          </DocIcon>{' '}
          in the sidebar, then open the <strong>Invite</strong>{' '}
          <DocIcon>
            <EnvelopeIcon aria-hidden="true" />
          </DocIcon>{' '}
          section.
        </li>
        <li>
          Choose <strong>Generate Invitation Code</strong>, then{' '}
          <strong>Copy Link</strong> and send it to the person you’re inviting.
        </li>
      </ol>
      <p>
        Whoever opens the link joins the workspace as an administrator, with full
        access to manage it (a Goalpost account is created for them first if they
        don’t have one). You can <strong>Revoke</strong> a code at any time.
      </p>
      <p>
        Adding a collaborator to a single document instead? Invite them to a team
        rather than as an administrator; that gives them access to just that team’s
        documents. See{' '}
        <Link to="/docs/teams" hash="invite">
          Invite someone to a team
        </Link>
        .
      </p>

      <h2 id="billing">Billing</h2>
      <p>
        Billing is managed per workspace by its administrators. From the
        workspace’s <strong>Billing</strong> section you can change your plan,
        update your payment method, and view invoices.
      </p>
      <p>
        Because each workspace bills separately, a client’s workspace and your own
        are never entangled, so you can be a paying owner of one and a free member
        of another at the same time. Upgrading takes effect immediately;
        downgrading applies at the end of the current billing period.
      </p>

      <h2 id="notifications">Notifications</h2>
      <p>
        Goalpost tells you when something needs your attention so you don’t have to
        keep checking. You’ll be notified when:
      </p>
      <ul>
        <li>
          a change is <strong>waiting on your approval</strong>, because you’re on
          a team whose sign-off it needs;
        </li>
        <li>
          a change you submitted has been <strong>approved</strong> and is now the
          official version, or was rejected and sent back for revision;
        </li>
        <li>
          you’ve been <strong>invited</strong> to a team, or added to a document.
        </li>
      </ul>
      <p>
        Notifications keep everyone aligned as the document changes: the
        author knows when their change lands, and approvers know when they’re
        holding one up.
      </p>
    </DocsLayout>
  );
}
